import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Chip,
  Box,
  MenuItem,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../../common/CommonLoader";
import { BatchUpdateClientInfo } from "../../../services/ClientsMaintenanceService";

// ⚠️ Field names MUST match the backend ClientBatchUpdateDto.Fields property names exactly:
//    billingContact, billingEmail, reportContact, reportEmail, shippingContact, shippingEmail,
//    billingPhone1, reportPhone1, shippingPhone1 (isActive is handled separately as a bool)
const FIELDS = [
  { label: "Billing Contact",   name: "billingContact" },
  { label: "Billing Email",     name: "billingEmail" },
  { label: "Report Contact",    name: "reportContact" },   // was "reportingContact" — FIXED
  { label: "Report Email",      name: "reportEmail" },     // was "reportingEmail"   — FIXED
  { label: "Shipping Contact",  name: "shippingContact" },
  { label: "Shipping Email",    name: "shippingEmail" },
  { label: "Billing Phone",     name: "billingPhone1" },
  { label: "Report Phone",      name: "reportPhone1" },
  { label: "Shipping Phone",    name: "shippingPhone1" },
];

// status select: "" = no change, "active" / "inactive" map to isActive true / false
const emptyForm = () => ({
  ...FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {}),
  status: "",
});

const populateFromClient = (client) => ({
  billingContact:  client?.billingContact  || "",
  billingEmail:    client?.billingEmail    || "",
  reportContact:   client?.reportContact   || "",   // was reportingContact — FIXED
  reportEmail:     client?.reportEmail     || "",   // was reportingEmail   — FIXED
  shippingContact: client?.shippingContact || "",
  shippingEmail:   client?.shippingEmail   || "",
  billingPhone1:   client?.billingPhone1   || "",
  reportPhone1:    client?.reportPhone1    || "",
  shippingPhone1:  client?.shippingPhone1  || "",
  status:          client ? (client.isActive ? "active" : "inactive") : "",
});

const BatchUpdateClients = ({ open, onClose, selectedClients = [], onSuccess }) => {
  const [updateMode, setUpdateMode] = useState("replace");
  const [formData, setFormData] = useState(emptyForm());
  const [loading, setLoading] = useState(false);

  // 1 client selected  → pre-fill with that client's current values
  // multiple clients   → blank form (leave blank = skip that field)
  useEffect(() => {
    if (open) {
      setFormData(
        selectedClients.length === 1
          ? populateFromClient(selectedClients[0])
          : emptyForm()
      );
      setUpdateMode("replace");
    }
  }, [open, selectedClients]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // Only send fields the user actually filled in; null = backend skips the field
    const fieldsPayload = FIELDS.reduce((acc, { name }) => {
      const trimmed = formData[name].trim();
      acc[name] = trimmed !== "" ? trimmed : null;
      return acc;
    }, {});
    fieldsPayload.isActive =
      formData.status === "" ? null : formData.status === "active";

    const filledCount = Object.values(fieldsPayload).filter((v) => v !== null).length;
    if (filledCount === 0) {
      NotificationManager.warning("Please enter at least one field to update.");
      return;
    }

    const payload = {
      accountNumbers: selectedClients.map((c) => c.accountNumber),
      updateMode,
      fields: fieldsPayload,
    };

    try {
      setLoading(true);
      await BatchUpdateClientInfo(payload);
      NotificationManager.success(
        `Successfully updated ${selectedClients.length} client(s).`
      );
      setFormData(emptyForm());
      onSuccess?.();
    } catch {
      NotificationManager.error("Error performing batch update.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(emptyForm());
    setUpdateMode("replace");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Batch Update Clients</DialogTitle>

      <DialogContent>
        <CommonLoader loading={loading} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Updating {selectedClients.length} client(s):
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
          {selectedClients.map((c) => (
            <Chip
              key={c.accountNumber}
              label={`${c.clientName} (${c.accountNumber})`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <FormControl sx={{ mb: 3 }}>
          <FormLabel>Update Mode</FormLabel>
          <RadioGroup
            row
            value={updateMode}
            onChange={(e) => setUpdateMode(e.target.value)}
          >
            <FormControlLabel
              value="replace"
              control={<Radio />}
              label="Replace — overwrite existing value"
            />
            <FormControlLabel
              value="add"
              control={<Radio />}
              label="Add — append to existing value"
            />
          </RadioGroup>
        </FormControl>

        {/* Status: always a straight set, independent of Update Mode */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Client Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              variant="outlined"
              size="small"
            >
              <MenuItem value="">No change</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedClients.length === 1
            ? "Fields are pre-filled with the selected client's current values."
            : "Leave a field blank to skip updating it."}
        </Typography>

        {/* Billing */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Billing
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Billing Contact"
              name="billingContact"
              value={formData.billingContact}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Billing Email"
              name="billingEmail"
              value={formData.billingEmail}
              onChange={handleChange}
              variant="outlined"
              size="small"
              type="email"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Billing Phone"
              name="billingPhone1"
              value={formData.billingPhone1}
              onChange={handleChange}
              variant="outlined"
              size="small"
              type="tel"
            />
          </Grid>
        </Grid>

        {/* Reporting */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Reporting
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Report Contact"
              name="reportContact"
              value={formData.reportContact}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Report Email"
              name="reportEmail"
              value={formData.reportEmail}
              onChange={handleChange}
              variant="outlined"
              size="small"
              type="email"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Report Phone"
              name="reportPhone1"
              value={formData.reportPhone1}
              onChange={handleChange}
              variant="outlined"
              size="small"
              type="tel"
            />
          </Grid>
        </Grid>

        {/* Shipping */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Shipping
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Shipping Contact"
              name="shippingContact"
              value={formData.shippingContact}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Shipping Email"
              name="shippingEmail"
              value={formData.shippingEmail}
              onChange={handleChange}
              variant="outlined"
              size="small"
              type="email"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Shipping Phone"
              name="shippingPhone1"
              value={formData.shippingPhone1}
              onChange={handleChange}
              variant="outlined"
              size="small"
              type="tel"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Update {selectedClients.length} Client(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchUpdateClients;